import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { FileUpload, fileToUrl } from "@/components/FileUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Palette, Sun, Moon, Monitor, ShieldAlert, Mail, MessageSquare, Check,
  User, Bell, Lock, AlertTriangle, Phone, Smartphone, ChevronLeft,
} from "lucide-react";
import { useTheme, ACCENT_OPTIONS, MODE_OPTIONS } from "@/lib/useTheme";
import InstallAppModal, { usePwaInstall } from "@/components/InstallAppModal";
import { usePush } from "@/lib/usePush";

// Support contact — used in cancel-subscription flow
const SUPPORT_WHATSAPP = "+966539098620";
const SUPPORT_EMAIL = "support@faisalfit.com";

export default function SettingsPage() {
  const { user, fetchMe, logout } = useAuth();

  // Theme
  const { mode, accent, setMode, setAccent } = useTheme();

  // Install-app modal state
  const [showInstall, setShowInstall] = useState(false);
  const { standalone } = usePwaInstall();

  // Profile
  const [name, setName] = useState(user?.full_name || "");
  const [avatar, setAvatar] = useState(user?.avatar_url || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [notif, setNotif] = useState(user?.notification_prefs || { email: true, community: true });
  const [savingProfile, setSavingProfile] = useState(false);

  // Password
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  // Cancel-subscription dialog
  const [cancelOpen, setCancelOpen] = useState(false);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/users/me", { full_name: name, avatar_url: avatar || null, phone: phone || null, notification_prefs: notif });
      await fetchMe();
      toast.success("تم حفظ التعديلات");
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingProfile(false); }
  };

  const changePassword = async () => {
    if (pwd.next.length < 8) { toast.error("كلمة المرور يجب أن تكون 8 أحرف على الأقل"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("كلمتا المرور غير متطابقتين"); return; }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      setPwd({ current: "", next: "", confirm: "" });
      toast.success("تم تحديث كلمة المرور");
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingPwd(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto lg:mx-0 pb-8" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-semibold" data-testid="settings-title">الإعدادات</h1>
        <p className="text-sm text-muted-foreground mt-1">خصّص تجربتك على المنصة.</p>
      </div>

      {/* ═════ Appearance ═════ */}
      <Card className="p-6 space-y-5" data-testid="settings-appearance">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold text-lg">المظهر</h2>
        </div>

        <div className="space-y-2">
          <Label className="text-sm">نمط الألوان</Label>
          <div className="grid grid-cols-3 gap-2">
            {MODE_OPTIONS.map((m) => {
              const Icon = m.key === "light" ? Sun : m.key === "dark" ? Moon : Monitor;
              const active = mode === m.key;
              return (
                <button
                  key={m.key}
                  onClick={() => setMode(m.key)}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 p-4 transition-all ${
                    active ? "border-primary bg-primary/5 shadow-sm" : "border-transparent bg-secondary hover:border-primary/30"
                  }`}
                  data-testid={`mode-${m.key}`}
                >
                  <Icon className={`h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                  <span className={`text-xs ${active ? "text-primary font-semibold" : "text-foreground"}`}>{m.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm">اللون الرئيسي</Label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {ACCENT_OPTIONS.map((a) => {
              const active = accent === a.key;
              return (
                <button
                  key={a.key}
                  onClick={() => setAccent(a.key)}
                  className={`relative rounded-xl h-14 flex flex-col items-center justify-center gap-1 border-2 transition-all ${
                    active ? "border-foreground shadow-md scale-[1.02]" : "border-transparent hover:scale-[1.02]"
                  }`}
                  style={{ backgroundColor: a.swatch + "15" }}
                  data-testid={`accent-${a.key}`}
                  aria-label={a.label}
                >
                  <span className="h-6 w-6 rounded-full shadow-inner" style={{ backgroundColor: a.swatch }} />
                  <span className="text-[10px] text-foreground/70">{a.label.split(" ")[0]}</span>
                  {active && (
                    <span className="absolute top-1 end-1 h-4 w-4 rounded-full bg-foreground text-background grid place-items-center">
                      <Check className="h-2.5 w-2.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground">يُطبَّق فوراً على كامل المنصة ويُحفظ على جهازك.</p>
        </div>
      </Card>

      {/* ═════ Install as App ═════ */}
      <Card className="p-5 sm:p-6" data-testid="settings-install-app">
        <button
          onClick={() => setShowInstall(true)}
          className="w-full flex items-center gap-4 text-start hover:opacity-90 transition-opacity"
          data-testid="btn-open-install-modal-settings"
        >
          <div className="h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 grid place-items-center shadow-md">
            <Smartphone className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold flex items-center gap-2">
              حمّل التطبيق على جوالك
              {standalone && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
                  مُثبّت ✓
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              اضغط لعرض خطوات التثبيت على iPhone وAndroid.
            </p>
          </div>
          <ChevronLeft className="h-4 w-4 text-muted-foreground rtl:-scale-x-100 shrink-0" />
        </button>
      </Card>

      {/* ═════ Profile ═════ */}
      <Card className="p-6 space-y-5" data-testid="settings-profile">
        <div className="flex items-center gap-2">
          <User className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold text-lg">الملف الشخصي</h2>
        </div>
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={avatar} />
            <AvatarFallback>{(name || "؟").slice(0, 1)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <FileUpload
              kind="image"
              value={avatar}
              onChange={(v) => setAvatar(fileToUrl(v))}
              label="الصورة الشخصية"
              testid="settings-avatar-upload"
            />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>الاسم الكامل</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" />
          </div>
          <div className="space-y-2">
            <Label>البريد الإلكتروني</Label>
            <Input value={user?.email} disabled />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>رقم الهاتف</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+9665..." data-testid="settings-phone" />
          </div>
        </div>
        <Button onClick={saveProfile} disabled={savingProfile} className="rounded-full" data-testid="btn-save-profile">
          {savingProfile ? "جارٍ الحفظ..." : "حفظ التعديلات"}
        </Button>
      </Card>

      {/* ═════ Push Notifications ═════ */}
      <PushNotificationsCard />

      {/* ═════ Password ═════ */}
      <Card className="p-6 space-y-4" data-testid="settings-password">
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold text-lg">كلمة المرور</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>كلمة المرور الحالية</Label>
            <Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} data-testid="settings-current-pw" />
          </div>
          <div className="space-y-2">
            <Label>الجديدة</Label>
            <Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} data-testid="settings-new-pw" />
          </div>
          <div className="space-y-2">
            <Label>تأكيد الجديدة</Label>
            <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="settings-confirm-pw" />
          </div>
        </div>
        <Button onClick={changePassword} disabled={savingPwd} className="rounded-full" data-testid="btn-change-password">
          {savingPwd ? "جارٍ التحديث..." : "تحديث كلمة المرور"}
        </Button>
      </Card>

      {/* ═════ Support (quick contact) ═════ */}
      <Card className="p-6 space-y-3" data-testid="settings-support">
        <div className="flex items-center gap-2">
          <Phone className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold text-lg">الدعم</h2>
        </div>
        <p className="text-sm text-muted-foreground">هل تحتاج مساعدة؟ فريقنا جاهز على الواتساب والبريد.</p>
        <div className="flex flex-wrap gap-2">
          <a
            href={`https://wa.me/${SUPPORT_WHATSAPP.replace(/[^\d]/g, "")}?text=${encodeURIComponent("السلام عليكم، أحتاج مساعدة بخصوص حسابي على منصة كوتش فيصل.")}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm transition-colors"
            data-testid="support-whatsapp"
          >
            <MessageSquare className="h-4 w-4" /> واتساب الدعم
          </a>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("استفسار")}`}
            className="inline-flex items-center gap-2 bg-secondary hover:bg-secondary/70 text-foreground px-4 py-2 rounded-full text-sm transition-colors"
            data-testid="support-email"
          >
            <Mail className="h-4 w-4" /> {SUPPORT_EMAIL}
          </a>
        </div>
      </Card>

      {/* ═════ Danger Zone — Cancel subscription ═════ */}
      <Card className="p-6 space-y-4 border-destructive/40" data-testid="settings-danger">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-destructive" />
          <h2 className="font-display font-semibold text-lg text-destructive">منطقة الخطر</h2>
        </div>
        <div className="rounded-lg bg-destructive/5 border border-destructive/20 p-4 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-destructive">إلغاء الاشتراك ومسح جميع البيانات</p>
              <p className="text-xs text-muted-foreground mt-1">
                يوقف وصولك لكل الدورات، الوصفات، المجتمع، والمحادثة مع المدرب. لا يمكن التراجع بعد التأكيد.
              </p>
            </div>
          </div>
        </div>
        <Button variant="destructive" onClick={() => setCancelOpen(true)} className="rounded-full" data-testid="btn-open-cancel">
          إلغاء الاشتراك
        </Button>
      </Card>

      <CancelSubscriptionDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        userEmail={user?.email}
        userName={user?.full_name}
      />
      <InstallAppModal open={showInstall} onOpenChange={setShowInstall} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cancel Subscription — warning + WhatsApp + Email pre-filled buttons
// ---------------------------------------------------------------------------
function CancelSubscriptionDialog({ open, onClose, userEmail, userName }) {
  const [confirmed, setConfirmed] = useState(false);

  const waMsg = `السلام عليكم،\nأرغب في إلغاء اشتراكي على البريد الإلكتروني: ${userEmail}\n${userName ? `الاسم: ${userName}\n` : ""}شكراً لكم.`;
  const emailBody = `السلام عليكم،\n\nأرغب في إلغاء اشتراكي على البريد الإلكتروني: ${userEmail}\n${userName ? `الاسم: ${userName}\n` : ""}\nمع خالص الشكر.`;
  const waUrl = `https://wa.me/${SUPPORT_WHATSAPP.replace(/[^\d]/g, "")}?text=${encodeURIComponent(waMsg)}`;
  const emailUrl = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("طلب إلغاء اشتراك")}&body=${encodeURIComponent(emailBody)}`;

  const close = () => { setConfirmed(false); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="cancel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="h-5 w-5" />
            {confirmed ? "تواصل مع الدعم لإتمام الإلغاء" : "تحذير قبل الإلغاء"}
          </DialogTitle>
        </DialogHeader>

        {!confirmed ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-destructive/5 border border-destructive/20 p-4 space-y-3">
              <p className="text-sm font-medium">عند إلغاء اشتراكك سيتم:</p>
              <ul className="text-sm space-y-1.5 text-muted-foreground list-disc ps-5">
                <li>إلغاء جميع الدورات التي اشتركت بها.</li>
                <li>إيقاف وصولك للوصفات الصحية.</li>
                <li>إلغاء صلاحيتك للمجتمع والمحادثة الخاصة مع المدرب.</li>
                <li>إيقاف جميع الفواتير الشهرية المستقبلية.</li>
                <li>مسح تقدّمك في الدورات ولا يمكن استرجاعه.</li>
              </ul>
            </div>
            <p className="text-xs text-muted-foreground">
              هل ما زلت ترغب في إلغاء اشتراكك؟ اضغط "نعم، إلغاء الاشتراك" للتواصل مع الدعم.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={close} data-testid="cancel-abort">تراجع</Button>
              <Button variant="destructive" onClick={() => setConfirmed(true)} data-testid="cancel-confirm">
                نعم، إلغاء الاشتراك ومسح البيانات
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              اضغط على أحد الخيارات أدناه — الرسالة جاهزة، فقط أرسل من تطبيق واتساب أو البريد.
            </p>

            <a
              href={waUrl}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-3 rounded-xl bg-green-600 hover:bg-green-700 text-white p-4 transition-colors group"
              data-testid="cancel-whatsapp"
              onClick={() => setTimeout(close, 500)}
            >
              <div className="h-10 w-10 rounded-full bg-white/20 grid place-items-center shrink-0">
                <MessageSquare className="h-5 w-5" />
              </div>
              <div className="flex-1 text-start">
                <div className="font-semibold">إرسال عبر واتساب</div>
                <div className="text-xs opacity-90">{SUPPORT_WHATSAPP}</div>
              </div>
              <span className="text-xs opacity-70 group-hover:opacity-100">فتح ←</span>
            </a>

            <a
              href={emailUrl}
              className="flex items-center gap-3 rounded-xl bg-secondary hover:bg-secondary/70 text-foreground p-4 transition-colors group"
              data-testid="cancel-email"
              onClick={() => setTimeout(close, 500)}
            >
              <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center shrink-0">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 text-start">
                <div className="font-semibold">إرسال عبر البريد</div>
                <div className="text-xs opacity-70">{SUPPORT_EMAIL}</div>
              </div>
              <span className="text-xs opacity-70 group-hover:opacity-100">فتح ←</span>
            </a>

            <div className="rounded-lg bg-muted/50 border p-3 space-y-1 text-xs">
              <p className="font-medium">معاينة الرسالة الجاهزة:</p>
              <pre className="whitespace-pre-wrap text-muted-foreground font-sans" data-testid="cancel-preview">{waMsg}</pre>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>إغلاق</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Push Notifications card (Settings)
// ---------------------------------------------------------------------------
function PushNotificationsCard() {
  const { supported, iosBlockedUntilInstalled, permission, subscribed, loading, subscribe, unsubscribe } = usePush();

  const onToggle = async (checked) => {
    if (checked) {
      const r = await subscribe();
      if (r.ok) toast.success("تم تفعيل الإشعارات");
      else if (r.reason === "install-first") toast.info("ثبّت التطبيق على iPhone أولاً ثم فعّل الإشعارات.", { duration: 6000 });
      else if (r.reason === "denied") toast.error("رفضت الإذن. فعّلها من إعدادات المتصفح لتظهر مرة أخرى.");
      else toast.error("تعذّر تفعيل الإشعارات");
    } else {
      const r = await unsubscribe();
      if (r.ok) toast.success("تم إيقاف الإشعارات على هذا الجهاز");
      else toast.error("تعذّر إيقاف الإشعارات");
    }
  };

  let hint = "خلّي وصفات جديدة، دروس، ورسائل المدرب توصلك مباشرة على جهازك.";
  if (!supported) hint = "متصفحك لا يدعم إشعارات الويب. جرّب Chrome أو Safari 16.4+ على iPhone بعد تثبيت التطبيق.";
  else if (iosBlockedUntilInstalled) hint = "لتفعيل الإشعارات على iPhone، ثبّت التطبيق أولاً من قسم «حمّل التطبيق».";
  else if (permission === "denied") hint = "الإذن مرفوض حالياً — فعّله من إعدادات المتصفح (اضغط على القفل بجانب الرابط ثم اسمح بالإشعارات).";

  const disabled = !supported || iosBlockedUntilInstalled || permission === "denied" || loading;

  return (
    <Card className="p-6 space-y-4" data-testid="settings-push">
      <div className="flex items-center gap-2">
        <Bell className="h-5 w-5 text-primary" />
        <h2 className="font-display font-semibold text-lg">إشعارات التطبيق</h2>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">تفعيل الإشعارات على هذا الجهاز</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{hint}</p>
        </div>
        <Switch
          checked={subscribed}
          onCheckedChange={onToggle}
          disabled={disabled}
          data-testid="settings-push-toggle"
        />
      </div>
      {subscribed && (
        <p className="text-[11px] text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1">
          <Check className="h-3 w-3" /> الإشعارات مفعّلة — ستصلك التنبيهات حتى لو كان التطبيق مغلقاً.
        </p>
      )}
    </Card>
  );
}
